import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import KeycloakService from 'services/KeycloakService';
import { GlobalContext } from 'providers/GlobalProvider';
import { AiWorkflowTrialUsage, CreditsAPI } from 'api/CreditsAPI';

const apiUrl = process.env.REACT_APP_PYAPI_URL as string;

export type WorkflowTrialBucket =
  | 'export'
  | 'import'
  | 'design'
  | 'beneficial_ownership';

const NO_TRIAL_RUNS_USED: AiWorkflowTrialUsage = {
  export_used: false,
  import_used: false,
  design_used: false,
  beneficial_ownership_used: false,
};

/**
 * Whether the selected team can afford one run of a workflow that charges `cost`
 * credits via CreditsAPI.spendCredits, i.e. whether the paid pool covers it.
 *
 * A team a super admin flagged unlimited_workflows (credits summary) is never
 * blocked: the server skips its workflow spends entirely.
 *
 * Fails OPEN: if the balance can't be read, `insufficient` stays false so a flaky
 * summary call never locks a paying team out (the spend itself is enforced
 * server-side). `refresh` re-reads the balance, e.g. after a run is charged.
 */
export const useWorkflowCredits = (cost: number, bucket?: WorkflowTrialBucket) => {
  const { cur_user } = useContext(GlobalContext);
  const teamId = cur_user?.selected_team?.id;
  const [remaining, setRemaining] = useState<number | null>(null);
  const [unlimited, setUnlimited] = useState(false);
  const [trialRuns, setTrialRuns] = useState<AiWorkflowTrialUsage>(NO_TRIAL_RUNS_USED);
  // Whether the run about to start is the team's free trial run for `bucket`
  // (team is trialing and hasn't used it). A ref, read once by consumeTrialRun
  // when the run completes, so it holds the state from BEFORE the server reserved
  // the trial and can't go stale in a completion callback's closure.
  const usesTrialRunRef = useRef(false);
  // Same value as state, so `insufficient` can stand down for the free trial run.
  const [usesTrialRun, setUsesTrialRun] = useState(false);

  const refresh = useCallback(async () => {
    if (!teamId) {
      return;
    }
    try {
      if (bucket) {
        // Same read-only check the server gate uses. Anything unverifiable counts
        // as "not a trial run", so the run is charged rather than given away.
        try {
          const { data } = await axios.get(`${apiUrl}/ai-workflows/trial-status`, {
            params: { group_id: teamId, bucket: bucket },
            headers: { Authorization: `Bearer ${KeycloakService.getToken()}` },
          });
          usesTrialRunRef.current = Boolean(data?.applicable && !data?.used);
        } catch {
          usesTrialRunRef.current = false;
        }
        setUsesTrialRun(usesTrialRunRef.current);
      }
      const summary = await CreditsAPI.getCreditsSummary(teamId);
      // Any flag the summary doesn't carry counts as not used.
      setTrialRuns({
        export_used: Boolean(summary?.result?.export_used),
        import_used: Boolean(summary?.result?.import_used),
        design_used: Boolean(summary?.result?.design_used),
        beneficial_ownership_used: Boolean(
          summary?.result?.beneficial_ownership_used,
        ),
      });
      if (summary?.success && summary.result?.unlimited_workflows) {
        setUnlimited(true);
        setRemaining(null);
        return;
      }
      setUnlimited(false);
      setRemaining(
        summary?.success
          ? Number(summary.result?.remaining_credits ?? 0)
          : null,
      );
    } catch (error) {
      console.error('[credits] Failed to read credit balance', error);
      setRemaining(null);
      setTrialRuns(NO_TRIAL_RUNS_USED);
    }
  }, [teamId, bucket]);

  // True once if the run that just completed was the free trial run, so the
  // caller skips CreditsAPI.spendCredits. Clears itself: a second run in the same
  // mount is charged unless `refresh` ran again before it started.
  const consumeTrialRun = useCallback((): boolean => {
    const wasTrialRun = usesTrialRunRef.current;
    usesTrialRunRef.current = false;
    return wasTrialRun;
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    remaining: remaining,
    trialRuns: trialRuns,
    consumeTrialRun: consumeTrialRun,
    // Never blocks the team's free trial run, which isn't charged.
    insufficient: !unlimited && !usesTrialRun && remaining !== null && remaining < cost,
    refresh: refresh,
  };
};
